import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { filtersSelector } from 'store/modules/competition-nav/selectors/filters-selector';
import { useGetMatchesQuery } from 'store/modules/api/afl-api';
import { groupMatchesByDate } from '../../utils/group-matches-by-date';
import { getGroupKeys } from '../../utils/get-group-keys';
import { filterByCity } from '../../utils/filter-by-city';

import { EmptyState } from 'common/react/components/EmptyState';

import { FixturesGroup } from './FixturesGroup';
import { ByesGroup } from './ByesGroup';
import { FixturesSkeleton } from './FixturesSkeleton';
import { FixturesLegend } from './FixturesLegend';

import { getTranslation } from 'common/react/utils/translations';

const LAST_BYE_ROUND = 23;

const checkIfBroadcasterGuide = (providerId) => {
    const translationString = getTranslation(
        `label.broadcastGuideURL.${providerId}`
    );

    if (translationString !== `label.broadcastGuideURL.${providerId}`) {
        return translationString;
    }

    return false;
};

export const FixturesList = ({ competitionId, compSeason, round }) => {
    const filters = useSelector(filtersSelector);

    const disableRounds =
        filters?.cities.length > 0 ||
        filters?.teams.length > 0 ||
        filters?.venues.length > 0;

    const broadcastGuideUrl =
        checkIfBroadcasterGuide(compSeason.providerId) ?? '';

    const { matches = [], isMatchesFetching } = useGetMatchesQuery(
        {
            pageSize: 100,
            competitionId: competitionId,
            compSeasonId: compSeason.id,
            ...(!disableRounds && { roundNumber: round.roundNumber }),
            ...(filters.teams.length && { teamId: filters.teams }),
            ...(filters.venues.length && { venueId: filters.venues })
        },
        {
            selectFromResult: ({ data, isFetching }) => ({
                matches:
                    data && filters.cities.length
                        ? filterByCity(data, filters.cities)
                        : data,
                isMatchesFetching: isFetching
            })
        }
    );

    const groups = useMemo(() => {
        // Group results based on start day/month
        if (matches) {
            return groupMatchesByDate(matches);
        }
    }, [matches]);

    const groupKeys = useMemo(() => {
        if (groups) {
            return getGroupKeys(groups);
        }
    }, [groups]);
    const roundNumber = round.roundNumber;

    const isConferenceSeason = matches.some(
        (match) => match?.home?.conference || match?.away?.conference
    );

    if (isMatchesFetching) {
        return <FixturesSkeleton />;
    }

    if (!matches || !matches.length) {
        return <EmptyState cssClass="competition-nav__empty-state" />;
    }

    return (
        <>
            {groupKeys && groups
                ? groupKeys.map((dateKey) => (
                      <FixturesGroup
                          key={dateKey}
                          dateKey={dateKey}
                          group={groups[dateKey]}
                          broadcastGuideUrl={broadcastGuideUrl}
                          disableRounds={disableRounds}
                      />
                  ))
                : null}

            {!isNaN(roundNumber) &&
            roundNumber < LAST_BYE_ROUND &&
            !disableRounds &&
            compSeason.id ? (
                <ByesGroup
                    compseasonId={compSeason.id}
                    roundNumber={roundNumber}
                />
            ) : null}

            {isConferenceSeason && <FixturesLegend />}
        </>
    );
};

FixturesList.propTypes = {
    competitionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    compSeason: PropTypes.object,
    round: PropTypes.object
};
